import React, { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import {
  ArrowLeftIcon,
  Bars3Icon,
  CreditCardIcon,
  UserCircleIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { useBreakpoint } from '@/hooks/useBreakpoint';

const adminNav = [
  { to: '/admin/planos', label: 'Planos', icon: CreditCardIcon },
  { to: '/admin/usuarios', label: 'Usuários', icon: UsersIcon },
  { to: '/admin/perfil', label: 'Meu perfil', icon: UserCircleIcon },
] as const;

export const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { isMobile } = useBreakpoint();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const initials = user?.nome?.charAt(0)?.toUpperCase() ?? 'A';

  const NavItems = ({ onNavigate }: { onNavigate?: () => void }) => (
    <div className="space-y-1">
      {adminNav.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-slate-700 text-white'
                : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
            )
          }
        >
          <Icon className="h-5 w-5 shrink-0 opacity-90" />
          {label}
        </NavLink>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-950">
      {/* Barra superior — mobile */}
      <div className="flex md:hidden items-center justify-between gap-2 px-4 py-3 bg-slate-900 border-b border-slate-800 shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">Painel administrativo</p>
          <p className="text-xs text-slate-400 truncate">Planos e usuários</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-white hover:bg-slate-800 shrink-0"
          onClick={() => setMobileNavOpen((o) => !o)}
          aria-expanded={mobileNavOpen}
          aria-label={mobileNavOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          {mobileNavOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
        </Button>
      </div>

      {/* Overlay mobile */}
      {isMobile && mobileNavOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-hidden
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'z-50 flex flex-col bg-slate-900 text-white border-slate-800 md:border-r md:w-60 md:shrink-0 md:min-h-screen',
          'fixed left-0 bottom-0 w-72 max-w-[85vw] transform transition-transform duration-200 md:static md:translate-x-0 md:max-w-none',
          'top-14 md:top-0 md:inset-y-0',
          isMobile && !mobileNavOpen && '-translate-x-full'
        )}
      >
        <div className="hidden md:block p-5 border-b border-slate-800">
          <p className="text-lg font-semibold tracking-tight">Painel administrativo</p>
          <p className="text-xs text-slate-400 mt-1">Gestão de planos e usuários</p>
        </div>

        <nav className="flex-1 p-3 md:p-4 overflow-y-auto">
          <NavItems onNavigate={() => setMobileNavOpen(false)} />
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-3">
          <Link
            to="/"
            onClick={() => setMobileNavOpen(false)}
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 shrink-0" />
            Voltar ao sistema
          </Link>
          <div className="flex items-center gap-3 pt-1">
            <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white truncate">{user?.nome ?? 'Administrador'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email ?? ''}</p>
            </div>
          </div>
          <Button type="button" variant="outline" className="w-full border-slate-600 text-slate-200 hover:bg-slate-800" onClick={logout}>
            Sair
          </Button>
        </div>
      </aside>

      {/* Conteúdo */}
      <main className="flex-1 min-h-0 overflow-y-auto bg-gray-50">
        <div className="w-full px-6 sm:px-8 lg:px-24 py-5">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
